import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ChevronLeft, Copy } from "lucide-react";
import Editor from "react-simple-code-editor";
import Prism from "prismjs";
import "prismjs/components/prism-markup";
import "prismjs/components/prism-javascript";
import "prismjs/components/prism-markup-templating";
import "prismjs/themes/prism.css";
import { Card, Button } from "../components/ui";
import { SelectCompactAntd } from "../components/SelectCompactAntd";
import developerAPI from "../api/developer-api";

const SCRIPT_TYPES = [
  { value: "essential", label: "Essential" },
  { value: "functional", label: "Functional" },
  { value: "analytics", label: "Analytics" },
  { value: "advertising", label: "Advertising" },
];

const SCRIPT_PLACEMENTS = [
  { value: "head", label: "HTML Head" },
  { value: "body_start", label: "HTML Body start" },
  { value: "body_end", label: "HTML Body end" },
];

const NAME_MAX_LENGTH = 25;

const initialForm = {
  name: "",
  scriptType: "functional",
  scriptPlacement: "body_end",
  code: "",
};

function scriptToForm(s) {
  if (!s) return initialForm;
  return {
    name: s.name || "",
    scriptType: s.script_type || "functional",
    scriptPlacement: s.script_placement || "body_end",
    code: s.code ?? "",
  };
}

export function EmbeddedScripts() {
  const { appId, scriptId: scriptIdParam } = useParams();
  const navigate = useNavigate();
  const isNew = scriptIdParam === "new" || !scriptIdParam;
  const scriptId = isNew ? null : parseInt(scriptIdParam, 10);

  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const updateForm = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setSaveError(null);
  };

  const handleNameChange = (e) => {
    const v = e.target.value;
    if (v.length <= NAME_MAX_LENGTH) updateForm("name", v);
  };

  const appSettingsState = { tab: "settings" };

  useEffect(() => {
    if (isNew) {
      setForm(initialForm);
      setLoading(false);
      return;
    }
    let cancelled = false;
    async function load() {
      try {
        setLoading(true);
        const script = await developerAPI.getAppScript(appId, scriptIdParam);
        if (cancelled) return;
        setForm(scriptToForm(script));
      } catch (err) {
        if (!cancelled) setSaveError(err.response?.data?.error || err.message || "Failed to load script.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [appId, scriptIdParam, isNew]);

  const handleCancel = () => navigate(`/apps/${appId}`, { state: appSettingsState });

  const handleSave = async () => {
    setSaveError(null);
    if (!form.name.trim()) {
      setSaveError("Name is required.");
      return;
    }
    setSaving(true);
    try {
      if (scriptId) {
        await developerAPI.updateAppScript(appId, scriptId, form);
      } else {
        await developerAPI.createAppScript(appId, form);
      }
      setSaveSuccess(true);
      setTimeout(() => {
        navigate(`/apps/${appId}`, { state: appSettingsState });
      }, 1500);
    } catch (err) {
      setSaveError(err.response?.data?.error || err.message || "Failed to save script.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="w-full p-8">
        <div className="animate-pulse rounded-lg h-8 bg-slate-100 w-48 mb-6" />
        <div className="animate-pulse rounded-lg h-64 bg-slate-50" />
      </div>
    );
  }

  return (
    <div className="w-full p-8">
      <div className="flex items-center justify-between mb-6">
        <Link
          to={`/apps/${appId}`}
          state={{ tab: "settings" }}
          className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-grigora-600 transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to App Settings
        </Link>
        <div className="flex items-center gap-3">
          <Button variant="secondary" onClick={handleCancel} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>

      {saveSuccess && (
        <div className="mb-4 py-3 px-4 rounded-lg bg-green-50 border border-green-200 text-green-800 text-sm">
          Script saved successfully. Redirecting to app settings…
        </div>
      )}
      {saveError && (
        <div className="mb-4 py-3 px-4 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm">
          {saveError}
        </div>
      )}

      <Card>
        <div className="p-6 space-y-6">
          {scriptId != null && (
            <div className="flex items-center gap-2 pb-2 border-b border-slate-100">
              <span className="text-slate-600 text-xs font-semibold">Script ID</span>
              <span className="font-mono text-sm text-slate-800">{scriptId}</span>
              <button
                type="button"
                onClick={() => navigator.clipboard.writeText(String(scriptId))}
                className="inline-flex items-center gap-1 px-2 py-1 rounded text-slate-500 hover:text-slate-700 hover:bg-slate-100 text-xs"
                title="Copy script ID"
              >
                <Copy size={12} /> Copy ID
              </button>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
            <div className="flex flex-col">
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-slate-700 text-xs font-semibold">Name</label>
                <span className="text-xs text-slate-400 tabular-nums">
                  {form.name.length}/{NAME_MAX_LENGTH}
                </span>
              </div>
              <input
                type="text"
                value={form.name}
                onChange={handleNameChange}
                maxLength={NAME_MAX_LENGTH}
                placeholder="e.g. Analytics script"
                className="w-full h-7 bg-white border border-slate-200 rounded pl-2.5 pr-2 text-[10px] text-slate-700 focus:outline-none focus:border-grigora-500 focus:ring-2 focus:ring-grigora-500/10"
                style={{ height: "32px" }}
              />
            </div>
            <SelectCompactAntd
              label="Script Type"
              value={form.scriptType}
              onChange={(val) => updateForm("scriptType", val)}
              options={SCRIPT_TYPES}
              width="w-full"
            />
            <SelectCompactAntd
              label="Script Placement"
              value={form.scriptPlacement}
              onChange={(val) => updateForm("scriptPlacement", val)}
              options={SCRIPT_PLACEMENTS}
              width="w-full"
            />
          </div>

          <div className="w-full">
            <div className="border border-slate-200 rounded-lg overflow-hidden bg-slate-50 flex max-h-[320px] w-full">
              {/* Code area – full width, scrolls horizontally and vertically, no wrap */}
              <div className="flex-1 min-w-0 w-full overflow-auto px-1">
                <Editor
                  value={form.code}
                  onValueChange={(code) => updateForm("code", code)}
                  highlight={(code) => Prism.highlight(code, Prism.languages.markup || Prism.languages.html, "markup")}
                  padding={12}
                  className="editor-light font-mono text-sm leading-[1.5] text-slate-800 w-full"
                  textareaClassName="focus:outline-none editor-textarea-full"
                  style={{
                    fontFamily: "ui-monospace, monospace",
                    fontSize: 13,
                    minHeight: "280px",
                    width: "100%",
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
